import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LoginRequest } from '../types/auth'
import logo from '../assets/neurologyx_logo.png'

export const LoginForm = () => {
  const { login, loading, error } = useAuth()
  const [credentials, setCredentials] = useState<LoginRequest>({
    username: '',
    password: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await login(credentials)
    } catch (err) {
      // Error handled by useAuth hook
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto" data-theme="cyber" style={{ backgroundColor: 'var(--login-bg)', backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.025) 1px, transparent 1px)', backgroundSize: '30px 30px' }}>
      {/* Ambient background light dynamics */}
      <div className="absolute -top-20 -left-20 w-72 h-72 sm:w-96 sm:h-96 rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: 'var(--login-glow-color)' }}></div>
      <div className="absolute -bottom-20 -right-20 w-72 h-72 sm:w-96 sm:h-96 rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: 'var(--login-glow-color)' }}></div>

      {/* Centralized Glassmorphism Card — layout moved to classes so the
          <700px-height variants can compact the card for the demo tablet. */}
      <div className="login-form-card rounded-2xl backdrop-blur-xl relative z-10 shadow-2xl w-1/2 max-w-[600px] min-w-[350px] p-6 [@media(max-height:700px)_and_(pointer:coarse)]:max-w-[440px] [@media(max-height:700px)_and_(pointer:coarse)]:p-5" style={{ backgroundColor: 'var(--login-card-bg)', boxShadow: '0 25px 50px -12px var(--login-glow-color), 0 0 0 1px var(--login-glow-color), 0 0 40px var(--login-glow-color)' }}>
        
        {/* Platform Branding */}
        <div className="flex flex-col items-center mb-4 [@media(max-height:700px)_and_(pointer:coarse)]:mb-3 select-none">
          <img src={logo} alt="Neuralogyx" className="w-28 sm:w-44 [@media(max-height:700px)_and_(pointer:coarse)]:w-32 h-auto object-contain mb-2" />
          <h1 className="text-lg sm:text-xl [@media(max-height:700px)_and_(pointer:coarse)]:text-base font-bold tracking-wide" style={{ color: 'var(--login-text-primary)' }}>NYOS</h1>
          <p className="text-xs mt-1 [@media(max-height:700px)_and_(pointer:coarse)]:mt-0.5 uppercase tracking-widest font-medium" style={{ color: 'var(--login-text-muted)' }}>Neuralogyx Yard Operating System</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form space-y-4 [@media(max-height:700px)_and_(pointer:coarse)]:space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2 [@media(max-height:700px)_and_(pointer:coarse)]:mb-1.5" style={{ color: 'var(--login-text-muted)' }}>Username</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              placeholder="yardmaster"
              autoComplete="username"
              className="w-full px-4 py-3 [@media(max-height:700px)_and_(pointer:coarse)]:py-2.5 border rounded-2xl text-base focus:outline-none focus:ring-2 focus:ring-[var(--login-accent-primary)] transition-all duration-200"
              style={{ backgroundColor: 'var(--login-input-bg)', borderColor: 'var(--login-input-border)', color: 'var(--login-text-primary)' }}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2 [@media(max-height:700px)_and_(pointer:coarse)]:mb-1.5" style={{ color: 'var(--login-text-muted)' }}>Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              placeholder="••••••••"
              autoComplete="current-password"
              className="w-full px-4 py-3 [@media(max-height:700px)_and_(pointer:coarse)]:py-2.5 border rounded-2xl text-base focus:outline-none focus:ring-2 focus:ring-[var(--login-accent-primary)] transition-all duration-200"
              style={{ backgroundColor: 'var(--login-input-bg)', borderColor: 'var(--login-input-border)', color: 'var(--login-text-primary)' }}
              required
            />
          </div>
          {/* Text-only error — a boxed alert grew the card ~60px and
              crushed the header/footer spacing on a failed attempt. */}
          {error && (
            <p className="text-sm text-red-400 text-center">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-[23px] [@media(max-height:700px)_and_(pointer:coarse)]:py-[13px] rounded-2xl font-semibold text-sm mt-6 [@media(max-height:700px)_and_(pointer:coarse)]:mt-4 focus:outline-none focus:ring-2 focus:ring-[var(--login-accent-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            style={{ background: 'linear-gradient(135deg, var(--login-accent-primary) 0%, var(--login-accent-secondary) 100%)', color: 'var(--login-text-primary)', boxShadow: '0 10px 15px -3px var(--login-glow-color)' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, var(--login-accent-primary) 0%, var(--login-accent-secondary) 100%)'}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          {/* Demo aid — prefills the seeded demo account (yardmaster /
              password from V9__insert_default_users.sql). Remove for prod. */}
          <button
            type="button"
            onClick={() => setCredentials({ username: 'yardmaster', password: 'password' })}
            className="w-full mt-2 text-xs font-medium tracking-wide hover:underline focus:outline-none"
            style={{ color: 'var(--login-text-muted)' }}
          >
            Use demo credentials
          </button>
        </form>

        <div className="pt-3 mt-4 [@media(max-height:700px)_and_(pointer:coarse)]:pt-2 [@media(max-height:700px)_and_(pointer:coarse)]:mt-2 text-center">
          <p className="text-xs font-medium tracking-wide" style={{ color: 'var(--login-text-muted)' }}>
            Secured Infrastructure &bull; Authorized Access Only
          </p>
        </div>
      </div>
    </div>
  )
}
